import { Component, OnInit, Input, EventEmitter, Output, ElementRef, ViewChild, TemplateRef, OnChanges, signal, NO_ERRORS_SCHEMA } from '@angular/core';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { IBaseOption } from '../../../../models/base.model';
import { Servico } from '../../../../models/catalogo-es.model';
import { ConfigSchemaFlowDefinition, ConfigSchemaSectionEDocsLocation } from '../../../../models/config-schema.model';
import { AccessLevelType } from '../../../../models/edocs.model';
import { FlowObjectType, FormSchema } from '../../../../models/flow-object.model';
import { FlowDefinition, FlowTarget, FlowTargetDescription } from '../../../../models/flow.model';
import { AuthService } from '../../../../services/auth.service';
import { CatalogoEsService } from '../../../../services/catalogo-es.service';
import { FlowObjectDefinitionService } from '../../../../services/flow-object-definition.service';
import { Enums } from '../../../../shared/enums';
import { Utils } from '../../../../shared/utils';
import { FDD_DEFAULT_TINYMCE_OPTIONS } from './tinymce-options';
import { CatalogServicesFilter } from './fdd-default.pipe';
import { IMPORTS } from './_imports';
import { Unidade } from '../../../../models/organograma.model';
import { AcessoCidadaoService } from '../../../../services/acesso-cidadao.service';
import { OrganogramaService } from '../../../../services/organograma.service';
import { FlowInboxDirectoryService } from 'src/app/services/flow-inbox-directory.service';
import { FlowInboxDirectory } from 'src/app/models/flow-inbox-directory.model';

@Component({
    selector: 'fdd-default',
    templateUrl: './fdd-default.component.html',
    styleUrl: './fdd-default.component.scss',
    standalone: true,
    imports: IMPORTS,
    schemas: [NO_ERRORS_SCHEMA]
})
export class FlowDefinitionDetailsDefaultComponent implements OnInit, OnChanges {
    // #region [ViewChild]
    @ViewChild('configSchemaRef') configSchemaRef: TemplateRef<HTMLElement>;
    @ViewChild('configSchemaEditorRef') configSchemaEditorRef: ElementRef;
    // #endregion

    // #region [Type props]
    FlowTarget = FlowTarget;
    FlowObjectType = FlowObjectType;
    AccessLevelType = AccessLevelType;
    FlowTargetDescription = FlowTargetDescription;
    TitleInfoType = Enums.TitleInfoType;
    Utils = Utils;
    // #endregion

    // #region [props]
    formFlowObjectSchemaFields: any[] = [];
    catalogServiceId = '';
    catalogServices: Servico[] = [];
    tinyMceOptions: any = FDD_DEFAULT_TINYMCE_OPTIONS;
    configSchemaDialog: MatDialogRef<HTMLElement, any>;
    isAdminAll = false;
    adminUnitsList: Unidade[] = [];
    // #endregion

    // #region [signals]
    $model = signal(null as FlowDefinition);
    $isReadOnlyMode = signal(false);
    $flowTargetOptions = signal([] as IBaseOption[]);
    $shouldLoadTinyMce = signal(false);
    $description = signal(null as string);
    $configSchema = signal(new ConfigSchemaFlowDefinition());
    $configSchemaFlat = signal(null as string);
    $titleInfoType = signal(null as Enums.TitleInfoType);
    $titleInfoType_Additional = signal(null as Enums.TitleInfoType);
    $formFieldOptions = signal([] as IBaseOption[]);
    $formField = signal(null as string);
    $formField_Additional = signal(null as string);
    $catalogServiceDisplay = signal('');
    $selectableCatalogServices = signal([] as IBaseOption[]);
    $shouldAddFlowInbox = signal(false);
    $configSchemaSectionEDocsLocation = signal(new ConfigSchemaSectionEDocsLocation());
    $flowInboxDirectoryList = signal([] as FlowInboxDirectory[]);
    // #endregion

    // #region [Input/Output]
    @Input() _model: FlowDefinition;
    @Input() _isReadOnlyMode: boolean;
    @Input() _flowTargetOptions: IBaseOption[];
    @Input() _shouldLoadTinyMce: boolean;
    @Input() _isCreate: boolean;
    @Output() _updateFlowDefinitionEvent = new EventEmitter<FlowDefinition>();
    @Output() _closeEvent = new EventEmitter<any>();
    @Output() _configSchemaChangeEvent = new EventEmitter<ConfigSchemaFlowDefinition>();
    // #endregion

    constructor(
        private dialog: MatDialog,
        private toastr: ToastrService,
        public authService: AuthService,
        private catalogServicesFilter: CatalogServicesFilter,
        private catalogoEsService: CatalogoEsService,
        private acessoCidadaoService: AcessoCidadaoService,
        private organogramaService: OrganogramaService,
        private flowObjectDefinitionService: FlowObjectDefinitionService,
        private flowInboxDirectoryService: FlowInboxDirectoryService,
    ) {
        if (this.authService.hasProfiles([Enums.Profiles.AdminAll])) {
            this.isAdminAll = true;
        }
    }

    ngOnInit() {
        let interval = setInterval(async () => {
            if (this.$model() == null) return;
            clearInterval(interval);

            const response = await this.acessoCidadaoService.getUnidadesPerfilAdministrador(this.authService.$user().id);
            if (!response.isSuccess) {
                this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }
            this.adminUnitsList = response.data;

            this.$description.set(this.$model().description);

            if (this.$model().configSchema != null) {
                Utils.setSignal(this.$configSchema, Object.assign(this.$configSchema(), JSON.parse(this.$model().configSchema)));
            }

            let formFlowObject = this.$model().flowObjectDefinitions.find(x => x.typeId == FlowObjectType.StartForm);
            if (formFlowObject != null && Utils.isNotNullOrEmpty(this.$model().ownerInfo)) {
                if (Utils.isNullOrEmpty(formFlowObject.formSchema)) {
                    const response = await this.flowObjectDefinitionService.getFormSchema(formFlowObject.id);

                    if (!response.isSuccess) {
                        this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                        return;
                    }

                    formFlowObject.formSchema = response.data;
                }

                let formSchema = JSON.parse(formFlowObject.formSchema) as FormSchema;
                this.formFlowObjectSchemaFields = Utils.getFormFieldsWithUsableValue(formSchema.components);
                this.$formFieldOptions.set(this.formFlowObjectSchemaFields.map(x => ({ value: x.key, description: x.label })));
            }

            this.initConfigSchemaInfo();

            if (this._isCreate) {
                let inboxStorage = window.localStorage.getItem(Enums.LocalStorageKeys.AdminFlowInbox);
                let inboxValue = '';
                if (inboxStorage != null) {
                    inboxValue = atob(inboxStorage);
                    Utils.updSignal(this.$model, x => {
                        x.unitId = inboxValue;
                        return x;
                    });
                }
            }

            this.getOrderedDirectoryList();

            //await this.getCatalogServices();
        }, 200);
    }

    ngOnChanges() {
        this.$model.set(this._model);
        this.$isReadOnlyMode.set(this._isReadOnlyMode);
        this.$flowTargetOptions.set(this._flowTargetOptions);
        this.$shouldLoadTinyMce.set(this._shouldLoadTinyMce);
    }

    titleInfoTypeChange(event?) {
        if (event != null) {
            this.$titleInfoType.set(event);
        }

        if (this.$titleInfoType() != Enums.TitleInfoType.FormField) {
            this.$formField.set(null);
        }
    }

    titleInfoTypeChange_Additional(event?) {
        if (event != null) {
            this.$titleInfoType_Additional.set(event);
        }

        if (this.$titleInfoType_Additional() != Enums.TitleInfoType.FormField) {
            this.$formField_Additional.set(null);
        }
    }

    titleInfoChange(event) {
        Utils.updSignal(this.$configSchema, x => {
            x.shouldAddInfoToEDocsTitles = event;
            return x;
        });

        if (!this.$configSchema().shouldAddInfoToEDocsTitles) {
            this.$titleInfoType.set(null);
            Utils.updSignal(this.$configSchema, x => {
                x.shouldAddInfoToEDocsTitles_Additional = false;
                x.shouldNotAddInfoToDocuments = false;
                return x;
            });
            this.titleInfoChange_Additional();
        }
    }

    titleInfoChange_Additional(event?) {
        if (event != null) {
            Utils.updSignal(this.$configSchema, x => {
                x.shouldAddInfoToEDocsTitles_Additional = event;
                return x;
            });
        }

        if (!this.$configSchema().shouldAddInfoToEDocsTitles_Additional) {
            this.$titleInfoType_Additional.set(null);
        }
    }

    shouldDisableAdditionalInfo(): boolean {
        return this.$isReadOnlyMode()
            || !this.$configSchema().shouldAddInfoToEDocsTitles
            || this.$titleInfoType() == null
            || (
                this.$titleInfoType() == Enums.TitleInfoType.FormField
                && Utils.isNullOrEmpty(this.$formField())
            );
    }

    shouldNotAddInfoToDocumentsChange(event) {
        Utils.updSignal(this.$configSchema, x => {
            x.shouldNotAddInfoToDocuments = event;
            return x;
        });
    }

    shouldIgnoreTargetBasedRoleFilteringChange(event) {
        Utils.updSignal(this.$configSchema, x => {
            x.shouldIgnoreTargetBasedRoleFiltering = event;
            return x;
        });
    }

    getOrderedAdminUnitsList() {
        return this.adminUnitsList.sort((a, b) => {
            let first = `${a?.organizacao?.organizacaoPai?.sigla || ''}${a?.organizacao?.sigla || ''}${a?.nomeCurto || ''}`.split(' - ').join('');
            let second = `${b?.organizacao?.organizacaoPai?.sigla || ''}${b?.organizacao?.sigla || ''}${b?.nomeCurto || ''}`.split(' - ').join('');
            return first.localeCompare(second);
        });
    }

    catalogServiceDisplayChange() {
        let filteredCatalogServices = this.catalogServicesFilter.transform(this.catalogServices, this.$catalogServiceDisplay());
        this.$selectableCatalogServices.set(filteredCatalogServices.map(x => ({ value: x.guid, description: x.nome })));
    }

    catalogServiceIdChange(event?: MatAutocompleteSelectedEvent) {
        if (event != null) {
            this.catalogServiceId = event.option.value.value;
        }

        if (this.catalogServiceId == '') {
            this.catalogServiceId = null;
            Utils.updSignal(this.$model, x => {
                x.catalogServiceId = null;
                return x;
            });
        } else {
            Utils.updSignal(this.$model, x => {
                x.catalogServiceId = this.catalogServiceId;
                return x;
            });
        }
    }

    displayCatalogServiceId(option: IBaseOption): string {
        return typeof option == 'string'
            ? option
            : Utils.isNotNullOrEmpty(option?.value)
                ? option.description
                : '';
    }

    clearCatalogService() {
        this.catalogServiceId = '';
        Utils.updSignal(this.$model, x => {
            x.catalogServiceId = null;
            return x;
        });
        this.$catalogServiceDisplay.set('');
        this.$selectableCatalogServices.set(this.catalogServices.map(x => ({ value: x.guid, description: x.nome })));
    }

    getCatalogServiceTooltip(): string {
        return this.$selectableCatalogServices().find(x => x.value == (this.catalogServiceId || this.$model().catalogServiceId))?.description;
    }

    getCatalogServicePlaceholder(): string {
        return this.$selectableCatalogServices().length == 0
            ? Enums.Messages.NoOptionAvailable
            : Enums.Messages.SelectAnOption;
    }

    showConfigSchema() {
        this.resolveConfigSchemaInfo();

        Utils.updSignal(this.$model, x => {
            x.description = this.$description();
            return x;
        });
        let tempObject = {
            model: Utils.decycleFlowDefinition(this.$model()),
            configSchema: JSON.parse(JSON.stringify(this.$configSchema()))
        };
        delete tempObject.model.configSchema;
        delete tempObject.model.flowObjectDefinitions;

        this.$configSchemaFlat.set(btoa(encodeURIComponent(JSON.stringify(tempObject))));

        this.configSchemaDialog = this.dialog.open(this.configSchemaRef, {
            minWidth: '800px'
        });

        this.configSchemaDialog.afterOpened().subscribe(() => {
            setTimeout(() => {
                this.configSchemaEditorRef.nativeElement.scrollLeft = 0;
                this.configSchemaEditorRef.nativeElement.scrollTop = 0;
            }, 1);
            setTimeout(() => {
                this.configSchemaEditorRef.nativeElement.style.opacity = '1';
            }, 100);
        });

        this.configSchemaDialog.afterClosed().subscribe(async () => {
            if (this.$isReadOnlyMode()) return;

            try {
                let modalObject = JSON.parse(decodeURIComponent(atob(this.configSchemaEditorRef.nativeElement.value)));
                delete modalObject.model.configSchema;

                // checa se o tipo da origem do conteúdo é o mesmo tipo do conteúdo atual (i.e. se a origem não é um FlowObjectDefinition)
                if (modalObject.model.targetId == null) {
                    this.toastr.error(Enums.Messages.InvalidConfigSchemaSourceType, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    return;
                }

                // checa se o código-fonte foi alterado
                if (JSON.stringify(tempObject) != JSON.stringify(modalObject)) {
                    this.$configSchema.set(JSON.parse(JSON.stringify(modalObject.configSchema)) as ConfigSchemaFlowDefinition);
                    Utils.updSignal(this.$model, x => {
                        x.catalogServiceId = modalObject.model.catalogServiceId;
                        x.description = modalObject.model.description;
                        x.name = modalObject.model.name;
                        x.publicName = modalObject.model.publicName;
                        x.targetId = modalObject.model.targetId;
                        x.unitId = modalObject.model.unitId;
                        return x;
                    });
                    this.$description.set(this.$model().description);
                }
            } catch (error) {
                this.toastr.error(Enums.Messages.InvalidFlowDefinitionConfigSchemaSource, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }

            this.initConfigSchemaInfo();

            //await this.getCatalogServices();
        });
    }

    nameChange(event) {
        Utils.updSignal(this.$model, x => {
            x.name = event;
            return x;
        });
    }

    publicNameChange(event) {
        Utils.updSignal(this.$model, x => {
            x.publicName = event;
            return x;
        });
    }

    flowTargetChange(event) {
        Utils.updSignal(this.$model, x => {
            x.targetId = event;
            return x;
        });

        Utils.updSignal(this.$configSchema, x => {
            x.whitelist = [];
            return x;
        });
    }

    async flowInboxChange(event) {
        if (event == null) return;

        const response = await this.organogramaService.getUnidade(event);
        if (!response.isSuccess) {
            this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }
        let unit = response.data;

        this.getOrderedDirectoryList();

        Utils.updSignal(this.$model, x => {
            x.unitId = event;
            x.unitInfo = `${unit.nomeCurto} - ${unit.nome}`;
            x.organizationInfo = `${unit.organizacao.sigla} - ${unit.organizacao.nomeFantasia}`;
            x.patriarchInfo = `${unit.organizacao.organizacaoPai.sigla} - ${unit.organizacao.organizacaoPai.razaoSocial}`;
            return x;
        });
    }

    onSubmit() {
        if (this.$isReadOnlyMode()) return false;
        if (!this.canSubmit()) return false;

        this.$description.set(Utils.removeBase64Images(this.$description()));
        Utils.updSignal(this.$model, x => {
            x.description = this.$description();
            return x;
        });

        if (this.$model().targetId == FlowTarget.Citizen) {
            Utils.updSignal(this.$configSchema, x => {
                x.shouldIgnoreTargetBasedRoleFiltering = false;
                return x;
            });
        }

        this.resolveConfigSchemaInfo();
        Utils.updSignal(this.$model, x => {
            x.configSchema = JSON.stringify(this.$configSchema());
            return x;
        });

        this._updateFlowDefinitionEvent.emit(this.$model());
    }

    closeForm() {
        this._closeEvent.emit();
    }

    async addFlowInbox(event) {
        this.adminUnitsList.push({
            guid: event.id,
            nomeCurto: event.flowInbox
        });

        this.adminUnitsList.sort((a, b) => {
            let first = a.nomeCurto;
            let second = b.nomeCurto;
            return first.localeCompare(second);
        });

        await this.flowInboxChange(event.id);
    }

    onUpdateEDocsLocation(event: ConfigSchemaSectionEDocsLocation) {
        const newLocation = Object.assign({}, this.$configSchemaSectionEDocsLocation(), event);
        Utils.setSignal(this.$configSchemaSectionEDocsLocation, newLocation);

        Utils.updSignal(this.$configSchema, x => {
            x.whitelist = [...x.whitelist, newLocation];
            return x;
        });

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    removeLocation(location: any) {
        if (this.$isReadOnlyMode()) return;

        Utils.updSignal(this.$configSchema, x => {
            x.whitelist = x.whitelist.filter(x => x.id != location.id);
            return x;
        });

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    removeAllLocations() {
        if (this.$isReadOnlyMode()) return;

        Utils.updSignal(this.$configSchema, x => {
            x.whitelist = [];
            return x;
        });

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    async getOrderedDirectoryList() {
        const response = await this.flowInboxDirectoryService.get();
        const directoryList = response.data || [];

        const directoryListFiltered = directoryList.filter(x => x.flowInboxId == this.$model().unitId);
        Utils.setSignal(this.$flowInboxDirectoryList, directoryListFiltered);

        const currentDirectoryId = this.$model().flowInboxDirectoryId;
        const existsInList = directoryListFiltered.some(dir => dir.id === currentDirectoryId);

        if (!existsInList) {
            Utils.updSignal(this.$model, x => {
                x.flowInboxDirectoryId = null;
                return x;
            });
        }
    }

    flowInboxDirectoryChange(directoryId: string) {
        Utils.updSignal(this.$model, x => {
                x.flowInboxDirectoryId = directoryId;
                return x;
            });
    }

    // #region [===== private methods =====]
    // #endregion

    private async getCatalogServices(event?){
        if (event != null) {
            Utils.updSignal(this.$model, x => {
                x.unitId = event;
                return x;
            });
        }

        let organizationId = event
            || this.$model().organizationId
            || this.adminUnitsList.find(x => x.guid == this.$model().unitId)?.organizacao?.guid
            || this.getOrderedAdminUnitsList()[0].organizacao.guid;

        const response = await this.catalogoEsService.getServicosOrganizacao(organizationId);

        if (response.isSuccess) {
            this.catalogServices = response.data || [];
            this.$selectableCatalogServices.set(this.catalogServices.map(x => ({ value: x.guid, description: x.nome })));

            if (event != null) {
                this.clearCatalogService();
                this.catalogServiceDisplayChange();
            } else {
                this.catalogServiceId = this.$model().catalogServiceId;
            }

            if (Utils.isNotNullOrEmpty(this.$model().catalogServiceId)) {
                this.$catalogServiceDisplay.set(this.$selectableCatalogServices().find(x => x.value == this.$model().catalogServiceId)?.description);
            }
        } else {
            this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
        }
    }

    private canSubmit() {
        if (
            this.$model().targetId == FlowTarget.Whitelist
            && this.$configSchema().whitelist.length == 0
        ) {
            this.toastr.warning(Enums.Messages.EmptyWhitelist, Enums.Messages.Warning, Utils.getToastrErrorOptions());
            return false;
        }

        return true;
    }

    private initConfigSchemaInfo() {
        this.$formField.set(this.$configSchema().formFieldNameAddedToEDocsTitles);
        if (this.$configSchema().shouldAddInfoToEDocsTitles) {
            this.$titleInfoType.set(this.$configSchema()?.addUserNameToEDocsTitles
                ? Enums.TitleInfoType.UserName
                : this.$configSchema()?.addUserCpfToEDocsTitles
                    ? Enums.TitleInfoType.UserCpf
                    : Utils.isNotNullOrEmpty(this.$configSchema()?.formFieldNameAddedToEDocsTitles)
                        ? Enums.TitleInfoType.FormField
                        : null
            );
        }
        this.titleInfoTypeChange();

        this.$formField_Additional.set(this.$configSchema().formFieldNameAddedToEDocsTitles_Additional);
        if (this.$configSchema().shouldAddInfoToEDocsTitles_Additional) {
            this.$titleInfoType_Additional.set(this.$configSchema()?.addUserNameToEDocsTitles_Additional
                ? Enums.TitleInfoType.UserName
                : this.$configSchema()?.addUserCpfToEDocsTitles_Additional
                    ? Enums.TitleInfoType.UserCpf
                    : Utils.isNotNullOrEmpty(this.$configSchema()?.formFieldNameAddedToEDocsTitles_Additional)
                        ? Enums.TitleInfoType.FormField
                        : null
            );
        }
        this.titleInfoTypeChange_Additional();
    }

    private resolveConfigSchemaInfo() {
        Utils.updSignal(this.$configSchema, x => {
            x.addUserNameToEDocsTitles = this.$titleInfoType() == Enums.TitleInfoType.UserName;
            x.addUserCpfToEDocsTitles = this.$titleInfoType() == Enums.TitleInfoType.UserCpf;
            x.formFieldNameAddedToEDocsTitles = this.$formField();
            x.addUserNameToEDocsTitles_Additional = this.$titleInfoType_Additional() == Enums.TitleInfoType.UserName;
            x.addUserCpfToEDocsTitles_Additional = this.$titleInfoType_Additional() == Enums.TitleInfoType.UserCpf;
            x.formFieldNameAddedToEDocsTitles_Additional = this.$formField_Additional();
            return x;
        });
    }
}
